import { useState, useEffect } from 'react';
import { api, AdminCredential } from '../../lib/api';
import { Icon, Spinner, CardTitle, StatusBadge } from '../../components/ui';

const providerLabels: Record<string, string> = {
  stripe: 'Stripe',
  murf: 'Murf.ai',
  elevenlabs: 'ElevenLabs',
  openai: 'OpenAI',
  aws: 'AWS',
};

function providerDot(credentials: AdminCredential[]) {
  const configured = credentials.filter((c) => c.is_configured);
  if (configured.length === 0) {
    return { background: '#34343B', boxShadow: 'none' };
  }
  const allValid =
    configured.length === credentials.length &&
    configured.every((c) => c.verification_status === 'valid');
  if (allValid) {
    return { background: '#7BD0A0', boxShadow: '0 0 10px rgba(123,208,160,.5)' };
  }
  return { background: 'var(--a-core)', boxShadow: '0 0 10px rgba(var(--a-rgb),.5)' };
}

export function AdminCredentials() {
  const [credentialsByProvider, setCredentialsByProvider] = useState<Record<string, AdminCredential[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    setLoading(true);
    const result = await api.adminGetCredentials();
    if (result.success && result.data) {
      setCredentialsByProvider(result.data.by_provider);
    } else {
      setError(result.error?.message || 'Failed to load credentials');
    }
    setLoading(false);
  };

  const handleEdit = (keyName: string) => {
    setEditingKey(keyName);
    setEditValue('');
    setMessage(null);
  };

  const handleSave = async () => {
    if (!editingKey || !editValue.trim()) return;

    setSaving(true);
    const result = await api.adminSetCredential(editingKey, editValue.trim());
    if (result.success) {
      setMessage({ type: 'success', text: 'Credential saved successfully' });
      setEditingKey(null);
      setEditValue('');
      await loadCredentials();
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to save credential' });
    }
    setSaving(false);
  };

  const handleClear = async (keyName: string) => {
    if (!confirm('Are you sure you want to clear this credential?')) return;

    const result = await api.adminClearCredential(keyName);
    if (result.success) {
      setMessage({ type: 'success', text: 'Credential cleared' });
      await loadCredentials();
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to clear credential' });
    }
  };

  const handleVerify = async (provider: string) => {
    setVerifying(provider);
    setMessage(null);

    const result = await api.adminVerifyProvider(provider);
    if (result.success) {
      setMessage({ type: 'success', text: result.data?.message || 'Credentials verified' });
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Verification failed' });
    }
    await loadCredentials();
    setVerifying(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-[#F0928C] text-sm">{error}</p>
        <button onClick={loadCredentials} className="btn-ghost mt-4">
          Retry
        </button>
      </div>
    );
  }

  const providers = Object.entries(credentialsByProvider);

  return (
    <div className="fx">
      <div className="flex items-center justify-between gap-3 mb-5">
        <p className="m-0 text-mist-400 text-[13px]">
          External API keys for payment and TTS providers
        </p>
        <button onClick={loadCredentials} className="btn-ghost px-3.5 py-2 text-xs flex-none">
          <Icon name="refresh" size={14} />
          Refresh
        </button>
      </div>

      {message && (
        <div
          className={`mb-5 px-4 py-3 rounded-xl border text-[13px] font-medium ${
            message.type === 'success'
              ? 'border-[#2E8B57]/40 bg-[#2E8B57]/10 text-[#7BD0A0]'
              : 'border-[#D8362F]/40 bg-[#D8362F]/10 text-[#F0928C]'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="flex flex-col gap-4">
        {providers.map(([provider, credentials]) => {
          const configuredCount = credentials.filter((c) => c.is_configured).length;
          return (
            <div key={provider} className="card">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="w-[9px] h-[9px] rounded-full flex-none"
                    style={providerDot(credentials)}
                  />
                  <CardTitle>{providerLabels[provider] || provider}</CardTitle>
                  <span
                    className={`badge ${
                      configuredCount === credentials.length ? 'badge-success' : 'badge-gray'
                    }`}
                  >
                    {configuredCount}/{credentials.length} configured
                  </span>
                </div>
                {['stripe', 'murf', 'elevenlabs', 'openai'].includes(provider) && (
                  <button
                    onClick={() => handleVerify(provider)}
                    disabled={verifying === provider}
                    className="btn-accent-outline gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {verifying === provider ? (
                      <>
                        <Spinner size={13} />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <Icon name="refresh" size={13} />
                        Verify credentials
                      </>
                    )}
                  </button>
                )}
              </div>

              <div>
                {credentials.map((cred) => (
                  <div
                    key={cred.id}
                    className="flex flex-wrap items-start justify-between gap-3 py-4 border-t border-ink-800"
                  >
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2">
                        <h4 className="m-0 text-white text-[13.5px] font-semibold">
                          {cred.display_name}
                        </h4>
                        {!!cred.is_required && (
                          <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[#F0928C]">
                            Required
                          </span>
                        )}
                      </div>
                      {cred.description && (
                        <p className="mt-0.5 mb-0 text-mist-500 text-xs">{cred.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2.5 mt-2.5">
                        {cred.is_configured ? (
                          <>
                            <code className="font-mono text-[11.5px] text-mist-300 bg-ink-950 border border-ink-700 rounded-md px-2 py-1">
                              {cred.masked_value || '********'}
                            </code>
                            <StatusBadge status={cred.verification_status} />
                          </>
                        ) : (
                          <span className="badge badge-warning">Not configured</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {editingKey === cred.key_name ? (
                        <>
                          <input
                            type="password"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="Enter value..."
                            className="input w-64 py-2 text-[13px]"
                            autoFocus
                          />
                          <button
                            onClick={handleSave}
                            disabled={saving || !editValue.trim()}
                            className="btn-grad px-3.5 py-2 text-[11px]"
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingKey(null)}
                            className="btn-ghost px-3.5 py-2 text-xs"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(cred.key_name)}
                            className="btn-ghost px-3.5 py-2 text-xs"
                          >
                            {cred.is_configured ? 'Update' : 'Configure'}
                          </button>
                          {!!cred.is_configured && (
                            <button
                              onClick={() => handleClear(cred.key_name)}
                              className="btn-ghost px-3 py-2 text-xs gap-1.5 !text-[#F0928C] hover:!border-[#D8362F]/50"
                              title="Clear credential"
                            >
                              <Icon name="trash" size={13} />
                              Clear
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {providers.length === 0 && (
          <p className="text-center text-mist-500 text-sm py-10 m-0">No credentials found</p>
        )}
      </div>
    </div>
  );
}
